import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Layout,
  Flex,
  Button,
  Tag,
  ConfigProvider,
  Typography,
  Tooltip,
  Select,
  Switch,
  Input,
} from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as Search } from '../../../assets/icons/search.svg';
import { ReactComponent as SearchBlack } from '../../../assets/icons/search-black.svg';
import { ReactComponent as Download } from '../../../assets/icons/download-blue.svg';
import { ReactComponent as DownloadDisabled } from '../../../assets/icons/download-disabled.svg';
import { ReactComponent as DownloadLight } from '../../../assets/icons/download-blue-light.svg';
import { ReactComponent as CloseBlack } from '../../../assets/icons/cancel-black.svg';
import { ReactComponent as Close } from '../../../assets/icons/cancel.svg';
import { ReactComponent as Reset } from '../../../assets/icons/reset-filter.svg';
import { ReactComponent as ResetBlack } from '../../../assets/icons/reset-filter-black.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/document-download.svg';
import { ReactComponent as DownloadLightIcon } from '../../../assets/icons/document-download-light.svg';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import LogsTable from '../../../components/LogsTable/LogsTable';
import CustomRangePicker from '../../../components/CustomRangePicker/CustomRangePicker';
import { DARK } from '../../../utilities/constants';
import { ReactComponent as Down } from '../../../assets/icons/down.svg';
import { ReactComponent as DownBlack } from '../../../assets/icons/down-black.svg';
import { useTheme } from '../../../context/ThemeContext';
import CustomMultiSelectDropdown from '../../../components/CustomMultiSelectDropdown/CustomMultiSelectDropdown';
import SearchBarWithFilter from '../../DriverMonitoringSystem/components/SearchBarWithFilter';
import EmptyCard from '../../../components/EmptyCard/EmptyCard';
import { getErrorMessage } from '../../../utilities/commonFunctions';

const { Content } = Layout;
const { Text } = Typography;

const getFilterLabel = (columns, filterKey, filterValue) => {
  const col = columns.find(col => col.dataIndex === filterKey);
  if (col?.selectFilter && Array.isArray(col?.selectFilterOptions)) {
    const selected = col.selectFilterOptions.find(
      opt => opt.value === filterValue
    );
    return selected ? selected.label : filterValue;
  }
  return filterValue;
};

const getFilterTitle = (columns, filterKey) =>
  columns.find(col => col.dataIndex === filterKey)?.title;

const getVisibleFilters = (filters, logsType) =>
  Object.keys(filters || {}).filter(
    filterKey =>
      filters[filterKey] &&
      !(
        (logsType === 'dms' && filterKey === 'anomaly_type') ||
        filterKey === 'true_dms_drowsy' ||
        filterKey === 'device_status'
      )
  );

const FilterTag = ({ filterKey, filterValue, columns, onClose }) => {
  const { theme } = useTheme();
  return (
    <Tag
      closable
      key={filterKey}
      style={{ padding: '2px 16px', display: 'flex', gap: 10 }}
      closeIcon={<Icon component={theme === DARK ? CloseBlack : Close} />}
      onClose={() => onClose(filterKey)}
    >
      <Flex vertical>
        <Text
          style={{
            fontSize: 12,
            lineHeight: '16px',
            color: 'var(--selected-text-color, #000000)',
          }}
        >
          {getFilterTitle(columns, filterKey)}
        </Text>
        <Text
          style={{
            fontSize: 12,
            lineHeight: '16px',
            color: 'var(--selected-text-color, #000000)',
          }}
        >
          {getFilterLabel(columns, filterKey, filterValue)}
        </Text>
      </Flex>
    </Tag>
  );
};

FilterTag.propTypes = {
  filterKey: PropTypes.string.isRequired,
  filterValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
  ]),
  columns: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
};

const FilterTagList = ({ tableParams, columns, logsType, clearFilters }) => {
  const visibleFilters = getVisibleFilters(tableParams?.filters, logsType);

  if (visibleFilters.length === 0) {
    return (
      <Text style={{ opacity: 0.5, fontSize: 16 }}>
        Your filters will appear here
      </Text>
    );
  }

  return (
    <>
      {visibleFilters.map(filterKey => (
        <FilterTag
          key={filterKey}
          filterKey={filterKey}
          filterValue={tableParams?.filters[filterKey]}
          columns={columns}
          onClose={clearFilters}
        />
      ))}
    </>
  );
};

FilterTagList.propTypes = {
  tableParams: PropTypes.shape({
    filters: PropTypes.object,
    sort: PropTypes.object,
    dateRange: PropTypes.array,
  }),
  columns: PropTypes.array.isRequired,
  logsType: PropTypes.string.isRequired,
  clearFilters: PropTypes.func.isRequired,
};

const LogsLayout = ({
  logsData,
  setLogsData,
  loading,
  tableParams,
  setTableParams,
  fetchData,
  formatData,
  columns,
  downloadLoading,
  handleDownload,
  expandedRowRenderCallback,
  onExpand,
  selectOptions,
  handleSelectChange,
  toggleStatus,
  handleToggleStatusChange,
  searchBarWithFilterSelectOptions,
  searchBarWithFilterSelectedOption,
  setSearchBarWithFilterSelectedOption,
  searchBarWithFilterSearchValue,
  setSearchBarWithFilterSearchValue,
  handleSearchBarWithFilterSelectChange,
  onSearchTextChange,
  handleSearch,
  skipSearchBarInitialEffect = false,
  download = true,
  downloadSmall = false,
  expandable = false,
  defaultSelectValue = 'All trips',
  logsType,
  selectedRow = null,
  enableSelectFilter = false,
  enableToggleFilter = false,
  enableTripIdSearch = false,
  toggleText = '',
  enableColumnSelect = false,
  onColumnsUpdate = () => {},
  enableSearchBarWithFilter = false,
  enableDateRange = true,
  additionalKeys = null,
  className = '',
  expandedRow = null,
}) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const { theme } = useTheme();

  const clearFilters = (resetKey = 'all') => {
    if (resetKey === 'all') {
      setTableParams(prev => ({
        ...prev,
        filters:
          logsType === 'dms' ? { anomaly_type: 'Driver drowsiness' } : {},
        sort: {},
      }));
    } else {
      delete tableParams?.filters?.[resetKey];
      setTableParams(prev => ({
        ...prev,
        filters: { ...prev.filters },
      }));
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetchData();
        if (response) {
          const formattedData = formatData(response);
          setLogsData(formattedData);
          setTableParams(prev => ({
            ...prev,
            pagination: {
              ...prev.pagination,
              total: response?.count,
            },
          }));
        }
      } catch (error) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch Logs! Please try again after sometime.'
          )
        );
      }
    };

    loadData();
  }, [fetchData]);

  const handleDownloadClick = () => {
    handleDownload({
      filter: tableParams?.filters,
      sort: tableParams?.sort,
      start_date: tableParams?.dateRange[0],
      end_date: tableParams?.dateRange[1],
      ...(additionalKeys && { ...additionalKeys }),
    });
  };

  const setDateRange = dateRange => {
    if (
      (dateRange[0] === '' && dateRange[1] === '') ||
      (dateRange[0] !== '' && dateRange[1] !== '')
    ) {
      setTableParams(prev => ({
        ...prev,
        dateRange: dateRange,
        pagination: {
          ...prev.pagination,
          current: 1,
        },
      }));
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Tag: {
            defaultBg: 'var(--button-default-bg-color, #0096e8)',
            colorBorder: 'var(--button-default-bg-color, #0096e8)',
            defaultColor: 'var(--selected-text-color, #000000)',
            borderRadiusSM: '1.25em',
            fontSize: '1rem',
          },
        },
      }}
    >
      <Content className="full-height content-logs">
        <Flex className="full-width" vertical gap={24}>
          <Flex gap={24} wrap>
            <Flex
              style={{
                flexGrow: 1,
                backgroundColor: theme === DARK ? '#232628' : '#D0D4D8',
                height: '100',
              }}
              align="center"
            >
              <Flex
                gap={8}
                style={{
                  height: 'max-content',
                  flexGrow: 1,
                  padding: '4px 16px',
                }}
                align="center"
              >
                <Flex
                  gap={8}
                  style={{
                    height: 'max-content',
                    flexGrow: 1,
                    padding: '4px 16px',
                  }}
                  align="center"
                >
                  <FilterTagList
                    tableParams={tableParams}
                    columns={columns}
                    logsType={logsType}
                    clearFilters={clearFilters}
                  />
                </Flex>
              </Flex>
              <Tooltip title="Reset all filters and sorting" placement="left">
                <Button
                  type="link"
                  style={{
                    backgroundColor: 'var(--table-row-expand-bg-color,#2E3033)',
                  }}
                  onClick={() => clearFilters('all')}
                  disabled={loading}
                >
                  <Icon component={theme === DARK ? Reset : ResetBlack} />
                </Button>
              </Tooltip>
            </Flex>
            <Flex gap={24} justify="flex-end" wrap="wrap">
              {enableSelectFilter && (
                <Select
                  defaultValue={defaultSelectValue}
                  options={selectOptions}
                  size="large"
                  onChange={handleSelectChange}
                  className="custom-select-dropdown"
                  notFoundContent={<EmptyCard />}
                  style={{
                    minWidth: 218,
                  }}
                  suffixIcon={
                    <Icon
                      component={theme === DARK ? Down : DownBlack}
                      style={{
                        pointerEvents: 'none',
                      }}
                    />
                  }
                  disabled={loading}
                />
              )}
              {enableToggleFilter && (
                <Flex align="center" gap={8} className="logs-toggle-div">
                  <Switch
                    checked={toggleStatus}
                    onChange={checked => {
                      handleToggleStatusChange(checked);
                    }}
                    className="legend-switch"
                    disabled={loading}
                  />
                  <Text>{toggleText}</Text>
                </Flex>
              )}
              {enableTripIdSearch && (
                <Input
                  onChange={onSearchTextChange}
                  size="large"
                  placeholder="Trip ID"
                  onPressEnter={() => handleSearch()}
                  suffix={
                    <Icon
                      component={theme === DARK ? Search : SearchBlack}
                      width={20}
                      height={20}
                      onClick={() => handleSearch()}
                    />
                  }
                  className="input-search"
                  style={{ width: '272px' }}
                />
              )}
              {enableColumnSelect && (
                <CustomMultiSelectDropdown
                  columns={columns}
                  onColumnsUpdate={onColumnsUpdate}
                  disabled={loading}
                />
              )}
              {enableSearchBarWithFilter && (
                <SearchBarWithFilter
                  selectOptions={searchBarWithFilterSelectOptions}
                  selectedOption={searchBarWithFilterSelectedOption}
                  setSelectedOption={setSearchBarWithFilterSelectedOption}
                  searchValue={searchBarWithFilterSearchValue}
                  setSearchValue={setSearchBarWithFilterSearchValue}
                  handleSelectFilterChange={
                    handleSearchBarWithFilterSelectChange
                  }
                  skipSearchBarInitialEffect={skipSearchBarInitialEffect}
                />
              )}
              {enableDateRange && (
                <CustomRangePicker
                  setDateRange={setDateRange}
                  disabled={loading}
                />
              )}
              {download && (
                <Button
                  icon={
                    downloadLoading || logsData?.length === 0 ? (
                      <Icon component={DownloadDisabled} />
                    ) : (
                      <Icon
                        component={theme === DARK ? Download : DownloadLight}
                      />
                    )
                  }
                  loading={downloadLoading}
                  disabled={downloadLoading || logsData?.length === 0}
                  onClick={handleDownloadClick}
                >
                  Download
                </Button>
              )}
              {downloadSmall && (
                <Button
                  icon={
                    downloadLoading || logsData?.length === 0 ? (
                      <Icon
                        component={DownloadDisabled}
                        style={{ fontSize: 24 }}
                      />
                    ) : (
                      <Icon
                        component={
                          theme === DARK ? DownloadIcon : DownloadLightIcon
                        }
                        style={{ fontSize: 24 }}
                      />
                    )
                  }
                  onClick={handleDownload}
                  disabled={downloadLoading || logsData?.length === 0}
                />
              )}
            </Flex>
          </Flex>

          <LogsTable
            data={logsData}
            columns={columns}
            tableParams={tableParams}
            setTableParams={setTableParams}
            loading={loading}
            expandable={expandable}
            expandedRowRenderCallback={expandedRowRenderCallback}
            onExpand={onExpand}
            selectedRow={selectedRow}
            className={className}
            expandedRow={expandedRow}
          />
        </Flex>
      </Content>
    </ConfigProvider>
  );
};

LogsLayout.propTypes = {
  logsData: PropTypes.array,
  setLogsData: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  tableParams: PropTypes.shape({
    pagination: PropTypes.object,
    filters: PropTypes.object,
    sort: PropTypes.object,
    dateRange: PropTypes.array,
  }),
  setTableParams: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  formatData: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  downloadLoading: PropTypes.bool,
  handleDownload: PropTypes.func,
  expandedRowRenderCallback: PropTypes.func,
  onExpand: PropTypes.func,
  selectOptions: PropTypes.array,
  handleSelectChange: PropTypes.func,
  toggleStatus: PropTypes.bool,
  handleToggleStatusChange: PropTypes.func,
  searchBarWithFilterSelectOptions: PropTypes.array,
  searchBarWithFilterSelectedOption: PropTypes.string,
  setSearchBarWithFilterSelectedOption: PropTypes.func,
  searchBarWithFilterSearchValue: PropTypes.string,
  setSearchBarWithFilterSearchValue: PropTypes.func,
  handleSearchBarWithFilterSelectChange: PropTypes.func,
  skipSearchBarInitialEffect: PropTypes.bool,
  download: PropTypes.bool,
  downloadSmall: PropTypes.bool,
  expandable: PropTypes.bool,
  defaultSelectValue: PropTypes.string,
  logsType: PropTypes.string,
  selectedRow: PropTypes.object,
  enableSelectFilter: PropTypes.bool,
  enableToggleFilter: PropTypes.bool,
  toggleText: PropTypes.string,
  enableColumnSelect: PropTypes.bool,
  onColumnsUpdate: PropTypes.func,
  enableSearchBarWithFilter: PropTypes.bool,
  enableDateRange: PropTypes.bool,
  additionalKeys: PropTypes.object,
};

export default LogsLayout;
